import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Landmark, Calculator, ArrowLeft } from "lucide-react";
import { Button } from "@/common/components/ui/button";
import { Card, CardContent } from "@/common/components/ui/card";
import { CompactCalculatorRow } from "../CompactCalculatorRow";
import { calculateHRA, formatCurrency } from "../../utils/calculations";
import ProductLayout from "@/common/components/ProductLayout";

export const HRACalculator = () => {
  const navigate = useNavigate();
  const [hraBasic, setHraBasic] = useState(50000);
  const [hraDA, setHraDA] = useState(0);
  const [hraReceived, setHraReceived] = useState(20000);
  const [hraRent, setHraRent] = useState(15000);
  const [hraMetro, setHraMetro] = useState(true);

  const hraResult = useMemo(
    () => calculateHRA(hraBasic, hraDA, hraReceived, hraRent, hraMetro),
    [hraBasic, hraDA, hraReceived, hraRent, hraMetro]
  );

  return (
    <ProductLayout>
      <div className="min-h-screen bg-background py-8">
        <div className="container mx-auto px-4 md:px-6">
          <div className="mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate("/calculators")}
              className="mb-4 pl-0 hover:bg-transparent hover:text-primary"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Calculators
            </Button>
            <div className="flex items-center gap-4">
              <div className="rounded-lg bg-primary/10 p-3 text-primary">
                <Calculator className="h-8 w-8" />
              </div>
              <h1 className="text-3xl font-bold text-secondary">
                HRA Calculator
              </h1>
            </div>
          </div>

          <div className="mx-auto max-w-7xl">
            <div className="grid gap-10 lg:grid-cols-2">
              <Card className="border-border bg-card">
                <CardContent className="space-y-6 p-6 md:p-8">
                  <div className="mb-2 flex items-center gap-3">
                    <div className="rounded-xl bg-orange-100 p-3">
                      <Landmark className="h-6 w-6 text-orange-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-secondary">
                        HRA Calculator
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Exemption Calculation
                      </p>
                    </div>
                  </div>
                  <div className="mb-4 flex justify-center">
                    <div className="flex rounded-xl bg-slate-100 p-1">
                      <button
                        onClick={() => setHraMetro(true)}
                        className={`rounded-lg px-6 py-2 text-sm font-bold transition-all ${hraMetro ? "bg-white text-primary shadow-sm" : "text-slate-500"}`}
                      >
                        Metro (50%)
                      </button>
                      <button
                        onClick={() => setHraMetro(false)}
                        className={`rounded-lg px-6 py-2 text-sm font-bold transition-all ${!hraMetro ? "bg-white text-primary shadow-sm" : "text-slate-500"}`}
                      >
                        Non-Metro (40%)
                      </button>
                    </div>
                  </div>
                  <div className="space-y-6">
                    <CompactCalculatorRow
                      label="Basic Salary (Yearly)"
                      value={hraBasic}
                      onChange={setHraBasic}
                      min={1000}
                      max={10000000}
                      step={1000}
                      prefix="₹"
                      colorClass="text-primary"
                    />

                    <CompactCalculatorRow
                      label="DA (Yearly)"
                      value={hraDA}
                      onChange={setHraDA}
                      min={0}
                      max={5000000}
                      step={1000}
                      prefix="₹"
                      colorClass="text-primary"
                    />

                    <CompactCalculatorRow
                      label="HRA Received (Yearly)"
                      value={hraReceived}
                      onChange={setHraReceived}
                      min={0}
                      max={5000000}
                      step={1000}
                      prefix="₹"
                      colorClass="text-primary"
                    />

                    <CompactCalculatorRow
                      label="Rent Paid (Yearly)"
                      value={hraRent}
                      onChange={setHraRent}
                      min={0}
                      max={5000000}
                      step={1000}
                      prefix="₹"
                      colorClass="text-primary"
                    />
                  </div>
                </CardContent>
              </Card>
              <Card className="border-border bg-muted/20">
                <CardContent className="p-8">
                  <div className="mb-8 text-center">
                    <p className="mb-1 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                      Exempted HRA
                    </p>
                    <h2 className="text-4xl font-black text-green-600">
                      {formatCurrency(hraResult.exemption)}
                    </h2>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between border-b border-slate-100 py-4">
                      <span className="font-medium text-muted-foreground">
                        Taxable HRA
                      </span>
                      <span className="text-xl font-bold text-red-500">
                        {formatCurrency(hraResult.taxable)}
                      </span>
                    </div>
                    <div className="rounded-xl bg-slate-50 p-4 text-xs leading-relaxed text-slate-500">
                      Least of following is exempt:
                      <br />
                      1. Actual HRA Received ({formatCurrency(hraReceived)})
                      <br />
                      2. Rent Paid - 10% of (Basic + DA) (
                      {formatCurrency(
                        Math.max(0, hraRent - (hraBasic + hraDA) * 0.1)
                      )}
                      )<br />
                      3. {hraMetro ? "50%" : "40%"} of (Basic + DA) (
                      {formatCurrency(
                        (hraBasic + hraDA) * (hraMetro ? 0.5 : 0.4)
                      )}
                      )
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </ProductLayout>
  );
};
